import { useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState as useReactState } from 'react'
import SafeHtml from '../../components/SafeHtml'
import type { AdminBlock, AdminBlockType, BlockWrite } from '../../services/adminService'
import RichTextEditor from './RichTextEditor'
import AssetUploadButton from '../AssetUploadButton'
import AssetPicker from '../AssetPicker'
import BlockStyleFields from './BlockStyleFields'
import type { BlockStyle } from '../../components/blocks/blockStyle'

const BLOCK_TYPES: { value: AdminBlockType; label: string }[] = [
  { value: 'hero', label: 'Hero banner' },
  { value: 'richtext', label: 'Rich text' },
  { value: 'html', label: 'Custom HTML' },
  { value: 'image', label: 'Image' },
  { value: 'button', label: 'Button / CTA' },
  { value: 'divider', label: 'Divider' },
  { value: 'columns', label: 'Columns (layout)' },
  { value: 'embed', label: 'Video embed' },
  { value: 'agenda', label: 'Agenda (dynamic)' },
  { value: 'speakers', label: 'Speakers (dynamic)' },
  { value: 'content', label: 'Content list (dynamic)' },
  { value: 'sponsors', label: 'Sponsors (dynamic)' },
  { value: 'eventinfo', label: 'Event Info (dynamic)' },
  { value: 'travel', label: 'Travel details (per-guest, email)' },
]

// Event Info sub-sections and their default-on state (welcome + venue on).
const EVENT_INFO_SECTIONS: { key: string; label: string; default: boolean }[] = [
  { key: 'welcome', label: 'Welcome message', default: true },
  { key: 'venue', label: 'Venue & map', default: true },
  { key: 'travel', label: 'Travel & parking', default: false },
  { key: 'dressCode', label: 'Dress code', default: false },
  { key: 'faqs', label: 'FAQs', default: false },
  { key: 'contacts', label: 'Key contacts', default: false },
]

type Cfg = Record<string, unknown>

export default function BlockEditor({
  eventId,
  block,
  onClose,
  onSave,
}: {
  eventId: string
  block: AdminBlock | null
  onClose: () => void
  onSave: (payload: BlockWrite) => void
}) {
  const [type, setType] = useState<AdminBlockType>(block?.blockType ?? 'richtext')
  const [config, setConfig] = useState<Cfg>(block?.config ?? {})
  // Richtext & custom-HTML are authored as HTML in `source`; seed from the
  // block's existing html so prior content edits round-trip.
  const [source, setSource] = useState<string>(block?.source || block?.html || '')
  const [initialHtml] = useState<string>(block?.source || block?.html || '')
  // Asset picker: holds a callback to receive the picked url.
  const [picker, setPicker] = useReactState<((url: string) => void) | null>(null)

  const set = (key: string, value: unknown) =>
    setConfig((c) => ({ ...c, [key]: value }))

  const str = (k: string) => (config[k] as string) ?? ''

  const save = () => {
    onSave({ blockType: type, config, source: source || null })
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{block ? 'Edit block' : 'Add block'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Block type"
            value={type}
            onChange={(e) => setType(e.target.value as AdminBlockType)}
            fullWidth
          >
            {BLOCK_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          {type === 'hero' && (
            <>
              <TextField label="Title" fullWidth value={str('title')} onChange={(e) => set('title', e.target.value)} />
              <TextField label="Subtitle" fullWidth value={str('subtitle')} onChange={(e) => set('subtitle', e.target.value)} />
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField label="Background image URL" fullWidth value={str('image')} onChange={(e) => set('image', e.target.value)} />
                <Button size="small" onClick={() => setPicker(() => (url: string) => set('image', url))}>
                  Browse
                </Button>
                <AssetUploadButton eventId={eventId} onUploaded={(url) => set('image', url)} />
              </Stack>
              <TextField label="Button label" fullWidth value={str('ctaLabel')} onChange={(e) => set('ctaLabel', e.target.value)} />
              <TextField label="Button link (sub-path or URL)" fullWidth value={str('ctaHref')} onChange={(e) => set('ctaHref', e.target.value)} />
            </>
          )}

          {type === 'richtext' && (
            <RichTextEditor
              valueHtml={initialHtml}
              onChangeHtml={setSource}
              onPickImage={() =>
                new Promise<string | null>((resolve) => {
                  setPicker(() => (url: string) => resolve(url))
                })
              }
            />
          )}

          {type === 'html' && (
            <>
              <TextField
                label="Custom HTML (sanitized on save)"
                fullWidth
                multiline
                minRows={6}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
              <SafeHtml
                html={source}
                sx={{ border: '1px dashed #ccc', borderRadius: 2, p: 1.5 }}
              />
            </>
          )}

          {type === 'image' && (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField label="Image URL" fullWidth value={str('url')} onChange={(e) => set('url', e.target.value)} />
                <Button size="small" onClick={() => setPicker(() => (url: string) => set('url', url))}>
                  Browse
                </Button>
                <AssetUploadButton eventId={eventId} onUploaded={(url) => set('url', url)} />
              </Stack>
              <TextField label="Alt text" fullWidth value={str('alt')} onChange={(e) => set('alt', e.target.value)} />
              <TextField label="Caption" fullWidth value={str('caption')} onChange={(e) => set('caption', e.target.value)} />
            </>
          )}

          {type === 'button' && (
            <>
              <TextField label="Label" fullWidth value={str('label')} onChange={(e) => set('label', e.target.value)} />
              <TextField
                select
                label="Action"
                fullWidth
                value={str('action') || 'link'}
                onChange={(e) => set('action', e.target.value)}
                helperText={
                  str('action') === 'register'
                    ? 'Links to this event’s public registration page.'
                    : str('action') === 'signin'
                    ? 'Links to this event’s branded sign-in page. For a one-click personal sign-in in an invite, use a custom link with the {{link}} token.'
                    : 'Use a custom URL, a sub-path (e.g. register), or the {{link}} token for a personal magic link.'
                }
              >
                <MenuItem value="link">Custom link</MenuItem>
                <MenuItem value="register">Register for this event</MenuItem>
                <MenuItem value="signin">Sign in to this event</MenuItem>
              </TextField>
              {(str('action') || 'link') === 'link' && (
                <TextField
                  label="Link (sub-path e.g. register, full URL, or {{link}})"
                  fullWidth
                  value={str('url')}
                  onChange={(e) => set('url', e.target.value)}
                />
              )}
              <TextField select label="Style" fullWidth value={str('variant') || 'contained'} onChange={(e) => set('variant', e.target.value)}>
                <MenuItem value="contained">Filled</MenuItem>
                <MenuItem value="outlined">Outlined</MenuItem>
              </TextField>
            </>
          )}

          {type === 'columns' && (
            <TextField
              select
              label="Number of columns"
              fullWidth
              value={String(config['columnCount'] ?? 2)}
              onChange={(e) => set('columnCount', Number(e.target.value))}
              helperText="Add blocks into each column after saving."
            >
              <MenuItem value="2">2 columns</MenuItem>
              <MenuItem value="3">3 columns</MenuItem>
            </TextField>
          )}

          {type === 'embed' && (
            <TextField
              label="YouTube or Vimeo URL"
              fullWidth
              value={str('url')}
              onChange={(e) => set('url', e.target.value)}
              helperText="Paste a normal video link; it's converted to a safe embed on save."
            />
          )}

          {(type === 'agenda' || type === 'speakers' || type === 'content' || type === 'sponsors' || type === 'eventinfo' || type === 'travel') && (
            <TextField label="Section title" fullWidth value={str('title')} onChange={(e) => set('title', e.target.value)} />
          )}
          {type === 'travel' && (
            <Typography variant="caption" color="text.secondary">
              Renders the recipient guest&apos;s travel &amp; lodging (flights,
              hotel, traveler numbers) in the &quot;Travel details&quot; email.
              Only appears for guests who have travel details set.
            </Typography>
          )}
          {type === 'eventinfo' && (
            <>
              <Typography variant="caption" color="text.secondary">
                Sections to show (welcome &amp; venue on by default). Each appears
                only if the event has that info.
              </Typography>
              <FormGroup>
                {EVENT_INFO_SECTIONS.map((s) => {
                  const saved = (config['sections'] as Record<string, boolean>) ?? {}
                  const checked = saved[s.key] ?? s.default
                  return (
                    <FormControlLabel
                      key={s.key}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(e) => {
                            // Persist the FULL resolved section map (defaults +
                            // saved + this change), so a default-on section the
                            // user never toggled isn't dropped from config — which
                            // made the renderer show only the explicitly-toggled
                            // sections (e.g. just "Travel").
                            const resolved: Record<string, boolean> = {}
                            for (const item of EVENT_INFO_SECTIONS) {
                              resolved[item.key] = saved[item.key] ?? item.default
                            }
                            resolved[s.key] = e.target.checked
                            set('sections', resolved)
                          }}
                        />
                      }
                      label={s.label}
                    />
                  )
                })}
              </FormGroup>
            </>
          )}
          {type === 'agenda' && (
            <TextField label="Max items (0 = all)" type="number" fullWidth value={String(config['limit'] ?? 0)} onChange={(e) => set('limit', Number(e.target.value))} />
          )}
          {type === 'speakers' && (
            <TextField select label="Which speakers" fullWidth value={config['featuredOnly'] ? 'featured' : 'all'} onChange={(e) => set('featuredOnly', e.target.value === 'featured')}>
              <MenuItem value="all">All speakers</MenuItem>
              <MenuItem value="featured">Featured only</MenuItem>
            </TextField>
          )}

          {type !== 'divider' && (
            <BlockStyleFields
              value={(config['style'] as BlockStyle) ?? {}}
              onChange={(style) => set('style', style)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save}>
          Save block
        </Button>
      </DialogActions>

      {picker && (
        <AssetPicker
          eventId={eventId}
          onClose={() => setPicker(null)}
          onPick={(url) => {
            picker(url)
            setPicker(null)
          }}
        />
      )}
    </Dialog>
  )
}
